import { create } from "zustand";
import { persist } from "zustand/middleware";

import { GroupParameter } from "@web-app/pages/common/OrdersSidebar/useOrderSidebar";
import { SortOptionType } from "@web-app/pages/common/OrdersSidebar/utils";
import {
  DriverEntity,
  DutyStatus,
  Order,
  OrderStatus,
  Route,
  User,
} from "@web-app/types";

export interface MinimizedConversation {
  conversationId: string;
  driverImage: string;
  driverName: string;
  driverUserId?: string;
  orderAppAvatarUrl?: string;
  orderDisplayId?: string;
  orderExternalId?: string;
}

const scheduledDateTimeStart = new Date();
scheduledDateTimeStart.setHours(0, 0, 0, 0);

const scheduledDateTimeEnd = new Date();
scheduledDateTimeEnd.setHours(23, 59, 59, 999);

export type AppFilterType = {
  assigneeIds: string[];
  countryCode: string;
  driverStatus: DriverEntity["dutyStatus"][];
  orderDeliveryType: Order["type"][];
  orderStatus: Order["status"][];
  scheduledDateTimeEnd: string;
  scheduledDateTimeStart: string;
  stateIds: string[];
  workspaceIds: string[];
};

const initialAppFilterData: AppFilterType = {
  assigneeIds: [],
  countryCode: "US",
  driverStatus: [DutyStatus.ONLINE, DutyStatus.INTRANSIT],
  orderDeliveryType: [],
  orderStatus: [
    OrderStatus.TODO,
    OrderStatus.INPROGRESS,
    OrderStatus.ASSIGNED,
    OrderStatus.COMPLETE,
  ],
  scheduledDateTimeEnd: scheduledDateTimeEnd.toISOString(),
  scheduledDateTimeStart: scheduledDateTimeStart.toISOString(),
  stateIds: [],
  workspaceIds: [],
};

export const getDefaultLocationForCountry = (
  countryCode: string,
): google.maps.LatLngLiteral => {
  switch (countryCode) {
    case "GB":
      return { lat: 51.5074, lng: -0.1278 }; // London
    case "IE":
      return { lat: 53.3498, lng: -6.2603 }; // Dublin
    default:
      return { lat: 40.7128, lng: -74.006 }; // New York
  }
};

export type OrderDetailsPanelTab =
  | "attachments"
  | "chat-history"
  | "doordash"
  | "driver-tracking"
  | "expedite"
  | "ezcater"
  | "onfleet"
  | "roadie"
  | "summary"
  | "timeline"
  | "uber";

interface DispatchStore {
  activeChatConversationId: string | null;
  activeChatDriverId: string | null;
  appFilter: AppFilterType;
  canViewOrderCard: boolean;
  canViewRouteCard: boolean;
  dateUpdateAt: string | null;
  directionsRenderer: google.maps.DirectionsRenderer | null;
  directionsService: google.maps.DirectionsService | null;
  dispatchMapRef: React.RefObject<google.maps.Map> | null;
  editRouteAssigneeId: string | null;
  editRouteOrderIds: string[];
  expandedRouteId: string | null;
  focusedAssigneeId: string | null;
  focusedOrderId: string | null;
  focusedRouteId: string | null;
  groupParameter: GroupParameter;
  isChatPanelOpen: boolean;
  isChatWingmanConversation: boolean;
  isEditOrder: boolean;
  isRouteOrdersOrderChanged: boolean;
  mapFocusedOrderId: string | null;
  mapFocusedRouteId: string | null;
  mapSettings: {
    isDriverLabelsVisible: boolean;
    isSatelliteView: boolean;
    isTrafficVisible: boolean;
  };
  minimizedConversations: MinimizedConversation[];
  orderDetailsPanelInitialActiveTab: OrderDetailsPanelTab;
  orderIdToAddRemoveFromRoute: string | null;
  pendingBroJobId: string | null;
  pendingChatDriverImage: string | null;
  pendingChatDriverName: string | null;
  pendingChatOrderDisplayId: string | null;
  pendingChatOrderId: string | null;
  pendingChatUserId: string | null;
  reportIssueOrderId: string | null;
  routeModalMode: "closed" | "create" | "edit";
  routeOrdersEditMode: "assign" | "none" | "unassign-remove" | "unassign";
  routes: Map<string, Route>;
  selectedOrderIds: Set<string>;
  selectedSidebarOrderIds: string[];
  selectedSidebarRouteIds: string[];
  showOrderDetailsPanel: boolean;
  sidebarMode: "list" | "table";
  sortParameter: SortOptionType;
  uberRouteIdAndDeliveryIdMap: Record<string, string>;
  user: Omit<User, "createdAt"> | null;
  userLatestLocation: google.maps.LatLngLiteral;
  userLatestZoomLevel: number | undefined;
}

const useDispatchStore = create<DispatchStore>()(
  persist(
    (): DispatchStore => ({
      activeChatConversationId: null,
      activeChatDriverId: null,
      appFilter: initialAppFilterData,
      canViewOrderCard: false,
      canViewRouteCard: false,
      dateUpdateAt: null,
      directionsRenderer: null,
      directionsService: null,
      dispatchMapRef: null,
      editRouteAssigneeId: null,
      editRouteOrderIds: [],
      expandedRouteId: null,
      focusedAssigneeId: null,
      focusedOrderId: null,
      focusedRouteId: null,
      groupParameter: {
        label: "Drivers & Connections",
        value: "connections-drivers",
      },
      isChatPanelOpen: false,
      isChatWingmanConversation: false,
      isEditOrder: false,
      isRouteOrdersOrderChanged: false,
      mapFocusedOrderId: null,
      mapFocusedRouteId: null,
      mapSettings: {
        isDriverLabelsVisible: false,
        isSatelliteView: false,
        isTrafficVisible: false,
      },
      minimizedConversations: [],
      orderDetailsPanelInitialActiveTab: "summary",
      orderIdToAddRemoveFromRoute: null,
      pendingBroJobId: null,
      pendingChatDriverImage: null,
      pendingChatDriverName: null,
      pendingChatOrderDisplayId: null,
      pendingChatOrderId: null,
      pendingChatUserId: null,
      reportIssueOrderId: null,
      routeModalMode: "closed",
      routeOrdersEditMode: "none",
      routes: new Map(),
      selectedOrderIds: new Set(),
      selectedSidebarOrderIds: [],
      selectedSidebarRouteIds: [],
      showOrderDetailsPanel: false,
      sidebarMode: "list",
      sortParameter: {
        disabled: false,
        label: "Pickup Time",
        value: "scheduledStartDateTime",
      },
      uberRouteIdAndDeliveryIdMap: {},
      user: null,
      userLatestLocation: getDefaultLocationForCountry(
        initialAppFilterData.countryCode,
      ),
      userLatestZoomLevel: 5,
    }),
    {
      name: "dispatchStore",
      partialize: (state) => ({
        appFilter: state.appFilter,
        dateUpdateAt: state.dateUpdateAt,
        groupParameter: state.groupParameter,
        mapSettings: state.mapSettings,
        sidebarMode: state.sidebarMode,
        sortParameter: state.sortParameter,
        userLatestLocation: state.userLatestLocation,
        userLatestZoomLevel: state.userLatestZoomLevel,
      }),
      version: 1.1, // Bump version to reset stored state
    },
  ),
);

export default useDispatchStore;
